import React, { ReactNode, useMemo } from 'react';
import { usePageQuery } from '#core/PageQuery';
import { DataPlusEmbargoContext } from '../contexts/DataplusEmbargoContext';
import { usePermissions } from '#core/Permissions';
import { PageQueryProps } from '#core/PageQuery/contexts/PageQueryContext';
import { useInstitution } from '#core/Institution';
import { useAuth } from '#core/Auth';

type GetAvailableSelectedYear = Pick<
  PageQueryProps,
  | 'isAfterEmbargoDate'
  | 'isAfterFreeEmbargoDate'
  | 'isAfterWurDetailsEmbargoDate'
> & {
  year: number;
  canViewMainEmbargo: boolean;
  canViewFreeEmbargo: boolean;
  isDetailsPage: boolean;
  canUseElevatedPermissions: boolean;
  useElevatedPermissions: boolean;
};

type GetAvailableYearOnYears = Pick<
  PageQueryProps,
  'isAfterEmbargoDate' | 'isAfterFreeEmbargoDate'
> & {
  selectedYear: number;
  canViewMainEmbargo: boolean;
  canViewFreeEmbargo: boolean;
  canUseElevatedPermissions: boolean;
  useElevatedPermissions: boolean;
};

type GetAvailableEmbargoYears = Pick<
  PageQueryProps,
  'isAfterEmbargoDate' | 'isAfterFreeEmbargoDate'
> & {
  canViewMainEmbargo: boolean;
  canUseElevatedPermissions: boolean;
  useElevatedPermissions: boolean;
};

const getLatestAvailableYear = (
  years: {
    [key: string]: Boolean;
  },
  selectedYear: number,
): number => {
  const yearKeys = Object.keys(years).map(Number); // Ensure keys are numbers
  yearKeys.sort((a, b) => b - a); // Sort in descending order

  const availableYear = yearKeys.find((year) => years[year]);

  return availableYear ?? selectedYear;
};

const getAvailableYearOnYears = ({
  isAfterEmbargoDate,
  isAfterFreeEmbargoDate,
  canViewFreeEmbargo,
  canViewMainEmbargo,
  selectedYear,
  canUseElevatedPermissions,
  useElevatedPermissions,
}: GetAvailableYearOnYears) => {
  const availableSelectedYears = Object.entries(isAfterEmbargoDate)
    .filter(([, value]) => value)
    .map(([year]) => Number(year));

  const embargoYears = Object.keys(isAfterEmbargoDate).map((year) =>
    Number(year),
  );
  const freeEmbargoYears = Object.keys(isAfterFreeEmbargoDate).map((year) =>
    Number(year),
  );

  if (canUseElevatedPermissions && useElevatedPermissions) {
    const index = embargoYears.indexOf(selectedYear);

    return embargoYears.slice(0, index + 1);
  }

  if (canViewMainEmbargo) {
    const index = embargoYears.indexOf(selectedYear);

    return embargoYears.slice(0, index + 1);
  }

  if (canViewFreeEmbargo) {
    if (isAfterFreeEmbargoDate[selectedYear]) {
      const index = freeEmbargoYears.indexOf(selectedYear);

      return freeEmbargoYears.slice(0, index + 1);
    }
  }

  if (availableSelectedYears.includes(selectedYear)) {
    const index = availableSelectedYears.indexOf(selectedYear);
    return availableSelectedYears.slice(0, index + 1);
  }

  return availableSelectedYears;
};

const getAvailableEmbargoYears = ({
  isAfterEmbargoDate,
  isAfterFreeEmbargoDate,
  canViewMainEmbargo,
  canUseElevatedPermissions,
  useElevatedPermissions,
}: GetAvailableEmbargoYears) => {
  const embargoYears = Object.keys(isAfterEmbargoDate).map((year) =>
    Number(year),
  );
  const freeEmbargoYears = Object.keys(isAfterFreeEmbargoDate)
    .filter((year) => isAfterFreeEmbargoDate[year])
    .map((year) => Number(year));

  if (
    (canUseElevatedPermissions && useElevatedPermissions) ||
    canViewMainEmbargo
  ) {
    return embargoYears.reverse();
  }

  return freeEmbargoYears.reverse();
};

const getAvailableSelectedYear = ({
  year,
  canViewMainEmbargo,
  canViewFreeEmbargo,
  isAfterEmbargoDate,
  isAfterFreeEmbargoDate,
  isAfterWurDetailsEmbargoDate,
  isDetailsPage,
  canUseElevatedPermissions,
  useElevatedPermissions,
}: GetAvailableSelectedYear) => {
  if (isAfterEmbargoDate[year]) {
    return year;
  }

  if (canUseElevatedPermissions && useElevatedPermissions) {
    return year;
  }

  if (!isDetailsPage) {
    if (!isAfterEmbargoDate[year] && canViewMainEmbargo) {
      return year;
    }

    if (isAfterFreeEmbargoDate[year] && canViewFreeEmbargo) {
      return year;
    }
  }

  if (
    isDetailsPage &&
    isAfterWurDetailsEmbargoDate[year] &&
    canViewMainEmbargo
  ) {
    return year;
  }

  return getLatestAvailableYear(isAfterEmbargoDate, year);
};

export const DataPlusEmbargoProvider = ({
  children,
  viewEmbargoAction,
  viewFreeEmbargoAction,
  isDetailsPage,
}: {
  children: ReactNode;
  viewEmbargoAction: string;
  viewFreeEmbargoAction: string;
  isDetailsPage: boolean;
}) => {
  const {
    query: {
      selectedYear: year,
      isAfterEmbargoDate,
      isAfterFreeEmbargoDate,
      isAfterWurDetailsEmbargoDate,
    },
  } = usePageQuery();

  const { institution } = useInstitution();

  const { hasResourcePermissionNew } = usePermissions();

  const { canUseElevatedPermissions, useElevatedPermissions } = useAuth();

  const canViewMainEmbargo = useMemo(
    () =>
      hasResourcePermissionNew({
        action: viewEmbargoAction,
        institutionId: institution.institutionId,
        year,
      }),
    [year, institution, viewEmbargoAction, hasResourcePermissionNew],
  );

  const canViewFreeEmbargo = useMemo(
    () =>
      hasResourcePermissionNew({
        action: viewFreeEmbargoAction,
        institutionId: institution.institutionId,
        year,
      }),
    [year, institution, viewFreeEmbargoAction, hasResourcePermissionNew],
  );

  const availableSelectedYear = getAvailableSelectedYear({
    year,
    canViewMainEmbargo,
    canViewFreeEmbargo,
    isAfterEmbargoDate,
    isAfterFreeEmbargoDate,
    isAfterWurDetailsEmbargoDate,
    isDetailsPage,
    canUseElevatedPermissions,
    useElevatedPermissions,
  });

  const availableYearOnYears = getAvailableYearOnYears({
    selectedYear: year,
    isAfterEmbargoDate,
    isAfterFreeEmbargoDate,
    canViewMainEmbargo,
    canViewFreeEmbargo,
    canUseElevatedPermissions,
    useElevatedPermissions,
  });

  // FYI: THIS IS USED IN THE TOP BAR TO DETERMINE WHICH YEARS TO SHOW IN THE YEAR DROPDOWN
  const availableEmbargoYears = getAvailableEmbargoYears({
    isAfterEmbargoDate,
    isAfterFreeEmbargoDate,
    canViewMainEmbargo,
    canUseElevatedPermissions,
    useElevatedPermissions,
  });

  return (
    <DataPlusEmbargoContext.Provider
      value={{
        availableSelectedYear,
        availableYearOnYears,
        availableEmbargoYears,
        canViewFreeEmbargo,
        canViewMainEmbargo,
      }}
    >
      {children}
    </DataPlusEmbargoContext.Provider>
  );
};
